import { ReactNode, useContext } from 'react';
import { Link as FluentLink, Body1Strong, Button, Caption1, Subtitle2 } from '@fluentui/react-components';
import { OpenRegular } from '@fluentui/react-icons';
import { Bubble, DateLabel, TabContent } from '@shared/components';
import { formatVancouver } from '@shared/backendFrontend';
import { TPermissions } from '@modules/access';
import { SignInContext } from '@modules/users';
import { TLiteratureContent } from '../types';
import { FIELD_LABELS } from '../constants';
import { getLiteratureAttachmentUrl, getTypeLabel } from '../helpers';
import FieldCell from './FieldCell';

type TProps = {
  content: TLiteratureContent;
  permissions: TPermissions;
};

const SPAN_CLASS: Record<string, string> = {
  url: 'md:col-span-2 lg:col-span-3',
  doi: 'md:col-span-2 lg:col-span-3',
  authors: 'md:col-span-2 lg:col-span-2',
  containerTitle: 'md:col-span-2 lg:col-span-2',
  publisher: 'md:col-span-1 lg:col-span-1',
};
const DEFAULT_SPAN = 'md:col-span-1 lg:col-span-1';

const LiteratureContent = ({ content, permissions }: TProps) => {
  const { organization } = useContext(SignInContext);
  const vancouver = formatVancouver(content);

  const attachmentUrl =
    content.attachmentBlobName && permissions.token
      ? getLiteratureAttachmentUrl(organization._id, content.attachmentBlobName, permissions.token, content.title)
      : undefined;

  // type and title are shown in the top block with the Vancouver preview, not in the grid.
  const fields: { key: string; render: () => ReactNode }[] = [
    { key: 'authors', render: () => content.authors?.join(', ') || null },
    { key: 'publicationYear', render: () => content.publicationYear ?? null },
    { key: 'containerTitle', render: () => content.containerTitle || null },
    { key: 'publisher', render: () => content.publisher || null },
    {
      key: 'doi',
      render: () =>
        content.doi ? (
          <FluentLink href={`https://doi.org/${content.doi}`} target="_blank">
            {content.doi}
          </FluentLink>
        ) : null,
    },
    {
      key: 'url',
      render: () =>
        content.url ? (
          <FluentLink href={content.url} target="_blank">
            {content.url}
          </FluentLink>
        ) : null,
    },
    {
      key: 'accessDate',
      render: () => (content.accessDate ? <DateLabel short>{content.accessDate}</DateLabel> : null),
    },
    { key: 'pmid', render: () => content.pmid || null },
    { key: 'volume', render: () => content.volume || null },
    { key: 'issue', render: () => content.issue || null },
    { key: 'pages', render: () => content.pages || null },
  ];

  return (
    <TabContent>
      <Bubble>
        <div className="flex flex-col space-y-6 w-full">
          {/* Type + Title with action buttons on the right (mirrors the assessment-card pattern). */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col space-y-1 min-w-0">
              <Caption1 className="text-neutral-500">{getTypeLabel(content.type)}</Caption1>
              <Subtitle2 className="break-words">{content.title}</Subtitle2>
            </div>
            {attachmentUrl && (
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  as="a"
                  href={attachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  appearance="transparent"
                  icon={<OpenRegular />}
                  aria-label="Open PDF"
                  className="!p-0"
                />
              </div>
            )}
          </div>

          {/* Vancouver preview - read-only, auto-generated from visible fields.
              Using <div> wrappers instead of Caption1 className="block" because
              Fluent's Caption1 is a <span> whose display can't be reliably overridden
              by a Tailwind utility. */}
          {vancouver && (
            <div className="border-l-2 border-neutral-300 pl-3 py-1">
              <div className="mb-1">
                <Caption1 className="text-neutral-500">Vancouver citation</Caption1>
              </div>
              <div>
                <Body1Strong className="break-words text-neutral-800">{vancouver}</Body1Strong>
              </div>
            </div>
          )}

          {/* Responsive metadata grid - 2 cols at md, 3 at lg. Each cell stacks
              label above value. grid-flow-dense lets short fields backfill
              holes left by wide (col-span-2/3) fields so the grid stays evenly
              packed regardless of which fields are present for the given type. */}
          <div className="grid grid-flow-dense grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
            {fields.map(({ key, render }) => {
              const rendered = render();
              if (rendered === null || rendered === undefined || rendered === '') return null;
              return (
                <FieldCell key={key} label={FIELD_LABELS[key] ?? key} spanClass={SPAN_CLASS[key] ?? DEFAULT_SPAN}>
                  {rendered}
                </FieldCell>
              );
            })}
          </div>
        </div>
      </Bubble>
    </TabContent>
  );
};

export default LiteratureContent;
